import React, {useState} from 'react';

import styled from 'styled-components';

import {FormErrorsProps, withFormErrors} from 'app/general/forms';
import StateButton from 'app/general/widgets/StateButton';
import {GuestUserToggleField} from 'app/user/components/CreateUserForm/GuestUserToggleField';
import {gt} from 'app/utils/i18n';

import type {ExtraPropsType} from 'app/components/general/SweetReactSelect';
import type {NewUserType} from 'app/user/types';
import type {UserSerializedType} from 'sweet-types';

interface InviteUserButtonProps {
  selectProps: {
    inputValue: string;
    // props passed on by parent Select component
    extraProps?: ExtraPropsType<UserSerializedType>;
  };
}

export const InviteUserButton = ({
  selectProps,
}: InviteUserButtonProps): JSX.Element => {
  const inputText = selectProps.inputValue;
  const {canInvite, onInvite, onLimit, selectLimitText} =
    selectProps.extraProps || {};

  const nameSpan = <span className="font-weight-bold">{inputText}</span>;
  return (
    <StyledInviteDiv>
      {onLimit ? (
        <small>{selectLimitText}</small>
      ) : inputText !== '' ? (
        <div className="d-flex justify-content-between align-items-center">
          <div>{gt('%s is not a member of this account.', nameSpan)}</div>
          {canInvite && onInvite ? (
            <button
              onClick={() => onInvite(inputText)}
              className="btn btn-secondary btn-sm btn-small"
            >
              {gt('Invite')}
            </button>
          ) : null}
        </div>
      ) : (
        <div>{gt('No available members found')}</div>
      )}
    </StyledInviteDiv>
  );
};

interface InviteProps extends FormErrorsProps {
  name: string;
  onSubmit: (userData: NewUserType) => Promise<any>;
  hideInviteForm: () => void;
}

export const InviteUserForm = withFormErrors<InviteProps>(
  ({
    name,
    hideInviteForm,
    formErrors,
    onSubmit,
    setFormErrors,
    captureFormErrorsFromRejection,
  }: InviteProps): JSX.Element => {
    const nameSpan = <span className="font-weight-bold">{name}</span>;
    const emailErrors = formErrors['email'];

    const [email, setEmail] = useState('');
    const [is_guest_until, set_is_guest_until] = useState<string | undefined>(
      undefined
    );

    const localOnSubmit = () =>
      onSubmit({
        email,
        name,
        is_guest_until,
      }).then(
        () => setFormErrors({}),
        (err) => {
          captureFormErrorsFromRejection(err);
          throw err;
        }
      );

    return (
      <StyledFloatingInviteDiv>
        <div className="d-flex justify-content-between align-items-center">
          <div>{gt('%s is not on this account.', nameSpan)}</div>
          <button
            type="button"
            onClick={hideInviteForm}
            className="btn btn-link btn-sm btn-small"
          >
            {gt('Cancel')}
          </button>
        </div>
        <hr className="mt-1 mb-2" />
        <div className="input-group input-group-sm input-append input-block-level mb-2">
          <input
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            type="email"
            autoFocus={true}
            className="form-control form-control-sm"
            placeholder={gt('Enter email to send invite')}
          />
          <div className="input-group-append add-on">
            <StateButton
              onClick={localOnSubmit}
              busyText={gt('Inviting...')}
              className="btn btn-secondary btn-sm"
            >
              {gt('Send Invite')}
            </StateButton>
          </div>
        </div>
        {emailErrors != null
          ? emailErrors.map((msg, idx) => (
              <div className="small text-danger pb-2" key={idx}>
                {msg}
              </div>
            ))
          : null}
        <GuestUserToggleField
          small={true}
          is_guest_until={is_guest_until}
          onChange={set_is_guest_until}
        />
      </StyledFloatingInviteDiv>
    );
  }
);

const StyledInviteDiv = styled.div`
  color: rgb(153, 153, 153);
  padding: 8px 10px 4px;
`;

const StyledFloatingInviteDiv = styled.div`
  border-radius: 4px;
  box-shadow: 0 0 0 1px hsla(0, 0%, 0%, 0.1), 0 4px 11px hsla(0, 0%, 0%, 0.1);
  position: absolute;
  width: 100%;
  z-index: 1;
  top: 100%;
  background: #fff;
  width: 100%;
  padding: 8px 10px;
`;
